import { useMemo } from 'react';
import { Chip, makeStyles } from '@material-ui/core';
import {
  AccountCircleRounded as AccountCircleIcon,
  EcoRounded as EcoIcon,
  VpnKeyRounded as VpnKeyIcon,
  BusinessCenterRounded as BusinessCenterIcon,
  CompareArrowsRounded as CompareArrowsIcon,
  HotelRounded as HotelIcon,
  BusinessRounded as BusinessIcon,
  PermContactCalendarRounded as PermContactCalendarIcon,
} from '@material-ui/icons';

import { UserRole, UserRoles } from '../../types/user-role.type';
import { USER_ROLE_LABELS } from '../../constants/user-role-labels.constants';

type UserRoleChipProps = {
  role: UserRole;
  size?: 'small' | 'medium';
};

const useStyles = makeStyles(() => ({
  root: (props: { backgroundColor: string; color: string }) => ({
    borderRadius: 4,
    textTransform: 'capitalize',
    backgroundColor: props.backgroundColor,
    color: props.color,

    '& .MuiChip-label': {
      display: 'flex',
      alignItems: 'center',
      gap: 6,

      '& > svg': {
        width: 16,
        height: 16,
        fill: props.color,
      },

      '& > span': {
        fontSize: 12,
        lineHeight: '14px',
      },
    },
  }),
}));

const getChipStyle = (type: UserRole): { backgroundColor: string; color: string; icon: React.ReactElement } => {
  switch (type) {
    case UserRoles.SUPER_ADMIN:
      return { backgroundColor: '#222831', color: '#ffffff', icon: <VpnKeyIcon /> };
    case UserRoles.ADMIN:
      return { backgroundColor: '#222831', color: '#ffffff', icon: <VpnKeyIcon /> };
    case UserRoles.ECO:
      return { backgroundColor: '#3D8D7A', color: '#ffffff', icon: <EcoIcon /> };
    case UserRoles.USER:
      return { backgroundColor: '#578FCA', color: '#ffffff', icon: <AccountCircleIcon /> };
    case UserRoles.SUBSCRIBER_BTOB:
      return { backgroundColor: '#EB5B00', color: '#ffffff', icon: <BusinessCenterIcon /> };
    case UserRoles.SUBSCRIBER_BTOC:
      return { backgroundColor: '#FFB200', color: '#ffffff', icon: <CompareArrowsIcon /> };
    case UserRoles.CONCIERGERIE:
      return { backgroundColor: '#4caf50', color: '#ffffff', icon: <HotelIcon /> };
    case UserRoles.CONTACT:
      return { backgroundColor: '#A08963', color: '#ffffff', icon: <PermContactCalendarIcon /> };
    case UserRoles.ENTREPRISE:
      return { backgroundColor: '#7886C7', color: '#ffffff', icon: <BusinessIcon /> };
  }
};

export function UserRoleChip({ role, size = 'small' }: UserRoleChipProps) {
  const { label, backgroundColor, color, icon } = useMemo(() => {
    return {
      label: USER_ROLE_LABELS[role],
      ...getChipStyle(role),
    };
  }, [role]);

  const classes = useStyles({ backgroundColor, color });

  return (
    <Chip
      className={classes.root}
      size={size}
      label={
        <>
          {icon}
          <span>{label}</span>
        </>
      }
    />
  );
}
